import React, { useState, useEffect } from 'react';
import InboxIngestPanel from '@/components/companies/InboxIngestPanel';
import HistoryImportPanel from '@/components/companies/HistoryImportPanel';
import { Building2, Plus, Settings, Users, Pencil, UserPlus, Trash2, AlertTriangle, Copy, RefreshCw, Mail, Download, Plug, Calculator } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { tenantAPI, accountingAPI, downloadBlob, unicontaOwnerAPI } from '../../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';

const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
};

// Seeded default Accountrix→Uniconta VAT-code map (passthrough of the standard Danish codes). The
// per-company mapping is editable — these are just the starting rows the editor shows.
const UNICONTA_VAT_DEFAULTS = { U25: 'U25', I25: 'I25', MOMSFRI: 'MOMSFRI', IKKEMOMS: 'IKKEMOMS' };

// Connection status wording. ONE source for both the portfolio table and the compact company card,
// so the two can never drift into describing the same client differently.
// `ownerCredentials` — whether the accountant has a server-user login stored at OWNER level. A
// company onboarded through /api/uniconta/owner carries NO per-company credentials by design, so
// without this flag such a company reads as unconfigured when it is fully working.
export function connectionStatus(tenant, { ownerCredentials = false } = {}) {
    const st = (tenant && tenant.settings) || {};
    const key = st.delivery_target || 'economic_basis';
    if (key === 'uniconta') {
        const mode = st.uniconta_delivery_mode === 'api' ? 'api' : 'csv';
        const hasCredentials = !!st.uniconta_server_user || ownerCredentials;
        // "Not configured" means a piece is genuinely missing — company, journal, or any usable
        // login. A company set up for CSV is configured; it just does not post over the API.
        if (!(st.uniconta_company_id && st.uniconta_journal_name && hasCredentials)) return 'Not configured';
        if (mode === 'csv') return 'CSV + bilag';
        return st.uniconta_integration_pack === false ? 'API push (no Integrationspakke)' : 'API push';
    }
    if (key === 'economic_plus') {
        return (st.economic_grant_token && st.economic_journal_number) ? 'Configured' : 'Not configured';
    }
    return st.scan_inbox_email ? 'Ready (CSV + scan inbox)' : 'CSV (no scan inbox)';
}

// Statuses that mean "this client cannot deliver yet" — rendered in the amber warning treatment.
export function connectionStatusIsWarning(status) {
    return status === 'Not configured'
        || status === 'CSV (no scan inbox)'
        || status === 'API push (no Integrationspakke)';
}

// Per-client accounting setup: VAT profile, the default contra (bank) account used to pre-fill
// Modkonto, and the per-fiscal-year starting bilag number that anchors numbering to e-conomic's
// real sequence. Local state so typing never fights the tenants list re-render. Saved via the
// same accounting-profile endpoint as the VAT profile.

/**
 * One row of the company card: a title, a summary of what is actually configured, and an Edit that
 * expands the inputs in place. Collapsed is the normal state — the card is read far more often
 * than it is filled in.
 *
 * `open`/`onOpen` are lifted so only ONE section is expanded at a time: the card's length was the
 * complaint, and letting every block open at once recreates it.
 */
function Section({ id, icon: Icon, title, summary, open, onOpen, tenantId, action = 'Edit',
                   children, alwaysOpen = false }) {
    const isOpen = alwaysOpen || open === id;
    return (
        <div className="py-2 border-t border-border/50 first:border-t-0 first:pt-0">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-xs font-medium flex items-center gap-1.5">
                        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                        {title}
                    </p>
                    {!isOpen && (
                        <p className="text-[11px] text-muted-foreground mt-0.5 truncate"
                           data-testid={`section-summary-${id}-${tenantId}`}>
                            {summary}
                        </p>
                    )}
                </div>
                {!alwaysOpen && (
                    <Button type="button" variant="ghost" size="sm"
                            className="h-6 px-2 text-[11px] shrink-0"
                            onClick={() => onOpen(isOpen ? null : id)}
                            data-testid={`section-edit-${id}-${tenantId}`}>
                        {isOpen ? 'Done' : action}
                    </Button>
                )}
            </div>
            {isOpen && <div className="mt-2 space-y-3" data-testid={`section-body-${id}-${tenantId}`}>{children}</div>}
        </div>
    );
}

function ClientAccountingPanel({ tenant, vatProfiles, deliveryTargets, ownerCredentials = null }) {
    const { refreshTenants } = useTenant();
    const { isAdmin } = useAuth();   // reset-test-data is a platform-admin developer tool only
    const settings = tenant.settings || {};
    const startingBilag = settings.starting_bilag || {};
    const [savingProfile, setSavingProfile] = useState(false);
    const [contra, setContra] = useState(settings.default_contra_account || '');
    const [savingContra, setSavingContra] = useState(false);
    const [bilagFy, setBilagFy] = useState('');
    const [bilagNo, setBilagNo] = useState('');
    const [savingNum, setSavingNum] = useState(false);
    const [scanInbox, setScanInbox] = useState(settings.scan_inbox_email || '');
    const [savingInbox, setSavingInbox] = useState(false);
    const [savingTarget, setSavingTarget] = useState(false);
    // Inbound "forward-to-inbox" address (Resend) — the client emails bilag here (reverse direction
    // from the e-conomic scan inbox above, which is where WE send exported bilag).
    const [inboundCode, setInboundCode] = useState(settings.inbound_code || '');
    const [inboundBusy, setInboundBusy] = useState(false);
    // Uniconta connection config (scaffold — no live auth) + manual-fallback downloads.
    const [uniCompanyId, setUniCompanyId] = useState(settings.uniconta_company_id != null ? String(settings.uniconta_company_id) : '');
    const [uniJournal, setUniJournal] = useState(settings.uniconta_journal_name || '');
    const [uniUser, setUniUser] = useState(settings.uniconta_server_user || '');
    const [uniPassword, setUniPassword] = useState('');   // write-only; never prefilled (encrypted server-side)
    const [uniVat, setUniVat] = useState({ ...UNICONTA_VAT_DEFAULTS, ...(settings.uniconta_vat_map || {}) });
    const [uniMode, setUniMode] = useState(settings.uniconta_delivery_mode === 'api' ? 'api' : 'csv');
    const [uniBusy, setUniBusy] = useState(false);
    const [uniDownloading, setUniDownloading] = useState('');
    // API push: always preview (dry run) before anything is posted.
    const [uniPushing, setUniPushing] = useState('');
    const [uniPreview, setUniPreview] = useState(null);   // dry-run payload
    const [uniResult, setUniResult] = useState(null);     // result of a real push

    const saveVatProfile = async (vatProfile) => {
        setSavingProfile(true);
        try {
            await tenantAPI.updateAccountingProfile(tenant.id, { vat_profile: vatProfile });
            toast.success('VAT profile updated');
            await refreshTenants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not update VAT profile');
        } finally { setSavingProfile(false); }
    };

    const saveDeliveryTarget = async (target) => {
        setSavingTarget(true);
        try {
            await tenantAPI.updateAccountingProfile(tenant.id, { delivery_target: target });
            toast.success('Delivery target updated');
            await refreshTenants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not update delivery target');
        } finally { setSavingTarget(false); }
    };

    const saveContra = async () => {
        const next = (contra || '').trim();
        if (next === (settings.default_contra_account || '')) return;
        setSavingContra(true);
        try {
            await tenantAPI.updateAccountingProfile(tenant.id, { default_contra_account: next });
            toast.success('Default contra account saved');
            await refreshTenants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save default contra account');
        } finally { setSavingContra(false); }
    };

    const saveStartingBilag = async () => {
        const fy = parseInt(bilagFy, 10);
        const no = parseInt(bilagNo, 10);
        if (!fy || Number.isNaN(no)) { toast.error('Enter both fiscal year and starting number'); return; }
        setSavingNum(true);
        try {
            await tenantAPI.updateAccountingProfile(tenant.id, { starting_bilag_fiscal_year: fy, starting_bilag_number: no });
            toast.success(`Starting bilag for ${fy} set to ${no}`);
            setBilagFy(''); setBilagNo('');
            await refreshTenants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save starting bilag number');
        } finally { setSavingNum(false); }
    };

    const saveScanInbox = async () => {
        const next = (scanInbox || '').trim();
        if (next === (settings.scan_inbox_email || '')) return;
        if (next && !next.includes('@')) { toast.error('Enter a valid scan inbox email'); return; }
        setSavingInbox(true);
        try {
            await tenantAPI.updateAccountingProfile(tenant.id, { scan_inbox_email: next });
            toast.success('Scan inbox email saved');
            await refreshTenants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save scan inbox email');
        } finally { setSavingInbox(false); }
    };

    const INBOUND_DOMAIN = 'bilag.accountrix.dk';
    const inboundAddress = inboundCode ? `${inboundCode}@${INBOUND_DOMAIN}` : '';

    const generateInboundCode = async (rotate = false) => {
        setInboundBusy(true);
        try {
            const r = await tenantAPI.generateInboundCode(tenant.id, rotate);
            setInboundCode(r.data?.inbound_code || '');
            toast.success(rotate ? 'New forwarding address generated' : 'Forwarding address created');
            await refreshTenants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not generate forwarding address');
        } finally { setInboundBusy(false); }
    };

    const copyInboundAddress = async () => {
        if (!inboundAddress) return;
        try {
            await navigator.clipboard.writeText(inboundAddress);
            toast.success('Address copied');
        } catch { toast.error('Could not copy — select the address and copy manually'); }
    };

    const uniConfigured = !!(settings.uniconta_company_id && settings.uniconta_journal_name);
    // A company onboarded through owner-level Uniconta onboarding carries NO per-company
    // credentials by design — the accountant's server-user login covers every company it can
    // reach. This badge used to read `!!settings.uniconta_server_user` alone, so such a company
    // reported "Configured (enter login)" and offered two empty inputs while it was in fact fully
    // connected and verified. `connectionStatus()` above already took ownerCredentials into
    // account; this inline copy had drifted from it, which is exactly what that function's comment
    // warns about.
    const perCompanyCreds = !!settings.uniconta_server_user;
    const ownerCredsKnown = ownerCredentials !== null;

    const uniCredsSet = perCompanyCreds || !!ownerCredentials;
    // Shown only when the accountant asks for it: a per-company login is an override of the
    // owner-level one, not the normal path.
    const [showPerCompanyLogin, setShowPerCompanyLogin] = useState(perCompanyCreds);
    // true / false / null-or-undefined = never checked (shown as neither available nor missing).
    const uniPack = settings.uniconta_integration_pack === true ? true
        : settings.uniconta_integration_pack === false ? false : null;

    const saveAndTestUniconta = async () => {
        if (!uniCompanyId.trim() || !uniJournal.trim()) {
            toast.error('Enter the Uniconta Company ID and journal name'); return;
        }
        setUniBusy(true);
        try {
            const payload = {
                uniconta_company_id: parseInt(uniCompanyId, 10),
                uniconta_journal_name: uniJournal.trim(),
                uniconta_vat_map: uniVat,
                uniconta_delivery_mode: uniMode,
            };
            if (uniUser.trim()) payload.uniconta_server_user = uniUser.trim();
            if (uniPassword) payload.uniconta_server_password = uniPassword;
            await tenantAPI.updateAccountingProfile(tenant.id, payload);
            setUniPassword('');
            await refreshTenants();
            // Test the live connection whenever a login is available: a per-company one we just
            // entered or already had, OR the owner-level server user that covers this company.
            // `perCompanyCreds` and not `uniCredsSet` on the left — uniCredsSet now includes the
            // owner-level login, which would make a half-typed username look like saved creds.
            const canTest = (uniUser.trim() && (uniPassword || perCompanyCreds))
                || (!uniUser.trim() && ownerCredentials);
            if (canTest) {
                try {
                    const r = await tenantAPI.unicontaTestConnection(tenant.id);
                    toast.success(`Connected to Uniconta — ${r.data?.company_name || 'company'} ✓`);
                    // Licence state is informational: a missing Integrationspakke never blocks the
                    // connection, it only means the API push path is unavailable for this company.
                    if (r.data?.integration_pack === false) {
                        toast.message('API posting unavailable for this company', {
                            description: 'No Integrationspakke on this Uniconta company. CSV + bilag delivery works.',
                        });
                    }
                } catch (te) {
                    toast.error(te.response?.data?.detail || 'Saved, but the Uniconta connection test failed — check the login.');
                }
            } else {
                toast.success('Uniconta configuration saved — enter the server-user login to connect.');
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save Uniconta configuration');
        } finally { setUniBusy(false); }
    };

    const unicontaPreviewPush = async () => {
        setUniPushing('preview'); setUniResult(null);
        try {
            const r = await tenantAPI.unicontaPush(tenant.id, true);
            setUniPreview(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kunne ikke hente forhåndsvisning');
            setUniPreview(null);
        } finally { setUniPushing(''); }
    };

    const unicontaDoPush = async () => {
        const n = uniPreview?.line_count ?? 0;
        if (!window.confirm(`Bogfør ${n} linje(r) i Uniconta-kladden "${settings.uniconta_journal_name || ''}"?`)) return;
        setUniPushing('push');
        try {
            const r = await tenantAPI.unicontaPush(tenant.id, false);
            setUniResult(r.data);
            setUniPreview(null);
            if (r.data?.status === 'posted') {
                toast.success(`Bogført ${r.data.line_count} linje(r), ${r.data.vouchers_attached ?? 0} bilag vedhæftet`);
            } else {
                toast.error(r.data?.message || 'Uniconta afviste bogføringen');
            }
            await refreshTenants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Bogføring mislykkedes');
        } finally { setUniPushing(''); }
    };

    const downloadUniconta = async (kind) => {
        setUniDownloading(kind);
        try {
            const res = kind === 'csv' ? await tenantAPI.unicontaCsv(tenant.id) : await tenantAPI.unicontaBilag(tenant.id);
            downloadBlob(res, kind === 'csv' ? 'uniconta_finanskladde.csv' : 'uniconta_bilag.zip');
        } catch (e) {
            const status = e.response?.status;
            const detail = e.response?.data?.detail || '';
            // "Nothing new to deliver" is the NORMAL outcome of a second download now that the
            // batch is incremental — it is information, not a failure, so it must not read as one.
            if (status === 400 && detail.startsWith('No approved')) {
                toast.info('Alt er allerede leveret — der er ingen nye bilag at eksportere.');
            } else if (status === 404) {
                toast.error('Ingen bilagsfiler fundet for de godkendte posteringer.');
            } else {
                toast.error(detail || 'Download mislykkedes');
            }
        } finally { setUniDownloading(''); }
    };

    const currentTarget = settings.delivery_target || 'economic_basis';
    const targetMeta = (deliveryTargets || []).find((t) => t.key === currentTarget);

    // The API push is only offered when the client is actually able to receive it: target uniconta,
    // mode api, and the Integrationspakke confirmed present. Anything else and the manual CSV +
    // bilag path below is the delivery route.
    // NOTE: must stay BELOW currentTarget — const is in the temporal dead zone until its
    // declaration runs, so reading it earlier throws during render and blanks the whole page.
    const uniCanPush = currentTarget === 'uniconta'
        && (settings.uniconta_delivery_mode === 'api')
        && settings.uniconta_integration_pack === true;

    // Only one section expanded at a time; null = all collapsed, which is the resting state.
    const [openSection, setOpenSection] = useState(null);

    // Summaries state the VALUES, so the accountant can verify the setup without opening anything.
    const targetLabel = (deliveryTargets || []).find((d) => d.key === currentTarget)?.label
        || currentTarget;
    const deliverySummary = currentTarget === 'uniconta'
        ? `${targetLabel} · ${uniMode === 'api' ? 'API push' : 'CSV + bilag'}`
          + (settings.uniconta_integration_pack === true ? ' · Integrationspakke'
             : settings.uniconta_integration_pack === false ? ' · no Integrationspakke' : '')
        : targetLabel;
    const vatLabel = (vatProfiles || []).find((v) => v.key === (settings.vat_profile
        || 'standard_vat_registered'))?.label || settings.vat_profile || 'Standard';
    const accountingSummary = [
        vatLabel,
        settings.default_contra_account ? `contra ${settings.default_contra_account}` : 'no contra set',
        Object.keys(startingBilag).length
            ? Object.entries(startingBilag).sort().map(([y, n]) => `${y}: from ${n}`).join(' · ')
            : 'no starting bilag',
    ].join(' · ');
    const intakeSummary = [
        inboundCode ? 'forwarding address active' : 'no forwarding address',
        currentTarget !== 'uniconta'
            ? (settings.scan_inbox_email ? `scan inbox ${settings.scan_inbox_email}` : 'no scan inbox')
            : null,
    ].filter(Boolean).join(' · ');

    // Destructive "reset test data" state
    const [resetOpen, setResetOpen] = useState(false);
    const [resetName, setResetName] = useState('');
    const [resetLearned, setResetLearned] = useState(false);
    const [resetting, setResetting] = useState(false);

    const doReset = async () => {
        setResetting(true);
        try {
            const r = await tenantAPI.resetTestData(tenant.id, resetName.trim(), resetLearned);
            const c = r.data?.cleared || {};
            toast.success(`Reset ${tenant.name}: ${c.documents || 0} docs, ${c.bank_lines || 0} bank lines, ${c.transactions || 0} transactions cleared`);
            setResetOpen(false); setResetName(''); setResetLearned(false);
            await refreshTenants();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reset failed');
        } finally { setResetting(false); }
    };

    // Don't let clicks inside the setup fields select the company card.
    return (
        <div className="space-y-3 mb-3" onClick={(e) => e.stopPropagation()}>
            {/* CONFIGURED CONNECTION, NOT A FORM.
                Every block below collapses to a summary line naming its actual values, with the
                inputs behind an Edit. One section is open at a time. The card is read far more
                often than it is configured — an accountant opens it to check a client, not to fill
                it in — so the default state shows what IS set rather than the means to set it. */}

            <Section id="delivery" icon={Plug} title="Delivery"
                     summary={deliverySummary} open={openSection} onOpen={setOpenSection}
                     tenantId={tenant.id}>
                <div>
                    <Label className="text-xs text-muted-foreground">Delivery target</Label>
                    <Select
                        value={currentTarget}
                        onValueChange={saveDeliveryTarget}
                        disabled={savingTarget}
                    >
                        <SelectTrigger className="h-8 mt-1" data-testid={`delivery-target-${tenant.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {/* Plain labels. The old "(API — not yet available)" suffix was derived from the
                                target's static status and is no longer true for Uniconta: whether direct
                                posting is available depends on the COMPANY's Integrationspakke, which the
                                note below reports per company. */}
                            {(deliveryTargets || []).map((t) => (
                                <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {targetMeta && targetMeta.status !== 'working' && targetMeta.fallback && (
                        <p className="text-[11px] text-amber-600 mt-1">{targetMeta.fallbackNote || 'Delivered via CSV — automatic API push not yet available.'}</p>
                    )}
                    {/* Uniconta: what delivery is actually possible is a property of the COMPANY, not of
                        the target, so the note is driven by the licence probe. Unchecked (null) says
                        nothing rather than guessing — see _fetch_client_chart / the connection test. */}
                    {currentTarget === 'uniconta' && settings.uniconta_integration_pack === true && (
                        <p className="text-[11px] text-muted-foreground mt-1" data-testid={`uniconta-delivery-note-${tenant.id}`}>
                            Direkte bogføring er tilgængelig for denne virksomhed.
                        </p>
                    )}
                    {currentTarget === 'uniconta' && settings.uniconta_integration_pack === false && (
                        <p className="text-[11px] text-amber-600 mt-1" data-testid={`uniconta-delivery-note-${tenant.id}`}>
                            Virksomheden har ikke Uniconta&apos;s Integrationspakke — levering sker via CSV + bilag nedenfor.
                        </p>
                    )}
                </div>
            </Section>

            <Section id="accounting" icon={Calculator} title="Accounting"
                     summary={accountingSummary} open={openSection} onOpen={setOpenSection}
                     tenantId={tenant.id}>
                <div>
                    <Label className="text-xs text-muted-foreground">VAT profile</Label>
                    <Select
                        value={settings.vat_profile || 'standard_vat_registered'}
                        onValueChange={saveVatProfile}
                        disabled={savingProfile}
                    >
                        <SelectTrigger className="h-8 mt-1" data-testid={`vat-profile-${tenant.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {vatProfiles.map((p) => (<SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs text-muted-foreground">Default contra (bank) account</Label>
                    <Input
                        className="h-8 mt-1"
                        placeholder="e.g. 5820"
                        value={contra}
                        onChange={(e) => setContra(e.target.value)}
                        onBlur={saveContra}
                        disabled={savingContra}
                        data-testid={`default-contra-${tenant.id}`}
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">Pre-fills Modkonto on lines with no contra set, and on export.</p>
                </div>
                <div>
                    <Label className="text-xs text-muted-foreground">Starting bilag number (per fiscal year)</Label>
                    <div className="flex items-center gap-2 mt-1">
                        <Input
                            className="h-8 w-24"
                            type="number"
                            placeholder="Year"
                            value={bilagFy}
                            onChange={(e) => setBilagFy(e.target.value)}
                            data-testid={`starting-bilag-fy-${tenant.id}`}
                        />
                        <Input
                            className="h-8 w-28"
                            type="number"
                            placeholder="Start no."
                            value={bilagNo}
                            onChange={(e) => setBilagNo(e.target.value)}
                            data-testid={`starting-bilag-no-${tenant.id}`}
                        />
                        <Button size="sm" className="h-8" onClick={saveStartingBilag} disabled={savingNum} data-testid={`starting-bilag-save-${tenant.id}`}>
                            {savingNum ? 'Saving…' : 'Set'}
                        </Button>
                    </div>
                    {Object.keys(startingBilag).length > 0 && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                            {Object.entries(startingBilag).sort().map(([y, n]) => `${y}: from ${n}`).join(' · ')}
                        </p>
                    )}
                </div>
            </Section>

            <Section id="intake" icon={Mail} title="Bilag intake"
                     summary={intakeSummary} open={openSection} onOpen={setOpenSection}
                     tenantId={tenant.id}>
                {currentTarget !== 'uniconta' && (
                <div>
                    <Label className="text-xs text-muted-foreground">Scan inbox email (e-conomic)</Label>
                    <Input
                        className="h-8 mt-1"
                        type="email"
                        placeholder="e.g. 951bilag1799739@e-conomic.dk"
                        value={scanInbox}
                        onChange={(e) => setScanInbox(e.target.value)}
                        onBlur={saveScanInbox}
                        disabled={savingInbox}
                        data-testid={`scan-inbox-${tenant.id}`}
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">On export, each bilag's original file is emailed here automatically.</p>
                </div>
                )}
                {/* Inbound forward-to address (Resend) — the client emails bilag IN. Reverse direction from
                    the e-conomic scan inbox above; kept visually separate so the two aren't confused. */}
                <div className="pt-3 mt-1 border-t border-border/60">
                    <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5" /> Forward bilag to Accountrix
                    </Label>
                    {inboundCode ? (
                        <>
                            <div className="flex items-center gap-2 mt-1">
                                <Input
                                    readOnly
                                    className="h-8 font-mono text-xs"
                                    value={inboundAddress}
                                    onFocus={(e) => e.target.select()}
                                    data-testid={`inbound-address-${tenant.id}`}
                                />
                                <Button type="button" size="sm" variant="outline" className="h-8 px-2"
                                        onClick={copyInboundAddress} title="Copy address"
                                        data-testid={`inbound-copy-${tenant.id}`}>
                                    <Copy className="h-4 w-4" />
                                </Button>
                                <Button type="button" size="sm" variant="ghost" className="h-8 px-2"
                                        onClick={() => generateInboundCode(true)} disabled={inboundBusy}
                                        title="Generate a new address (retires the current one)"
                                        data-testid={`inbound-rotate-${tenant.id}`}>
                                    <RefreshCw className={`h-4 w-4 ${inboundBusy ? 'animate-spin' : ''}`} />
                                </Button>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1">
                                Give this to your client — anything they email here lands in this company's inbox and is read by the AI. Emailed documents are flagged <span className="font-medium">“unverified sender”</span> until you confirm them in review.
                            </p>
                        </>
                    ) : (
                        <div className="mt-1">
                            <Button type="button" size="sm" variant="outline" className="h-8"
                                    onClick={() => generateInboundCode(false)} disabled={inboundBusy}
                                    data-testid={`inbound-generate-${tenant.id}`}>
                                {inboundBusy ? 'Generating…' : 'Generate forwarding address'}
                            </Button>
                            <p className="text-[11px] text-muted-foreground mt-1">
                                Create a unique <span className="font-mono">…@{INBOUND_DOMAIN}</span> address your client can forward invoices to.
                            </p>
                        </div>
                    )}
                </div>
                {/* e-conomic-only. Gated on "not Uniconta" rather than an allow-list so a future target
                    does not silently lose the field. HIDDEN, not disabled — the stored scan_inbox_email
                    is untouched, so switching target and back restores the configuration. */}
                {/* e-conomic only: the importer reads /accounting-years/{y}/entries and /journals/{n}/entries,
                    which have no Uniconta equivalent reachable today. Explicit trigger, never on connect. */}
                {currentTarget !== 'uniconta' && (
                <div className="mt-3 pt-3 border-t border-border/40">
                    <HistoryImportPanel tenant={tenant} />
                </div>
                )}
            </Section>

            {/* Uniconta connection (scaffold) — shown only when this client's target is Uniconta.
                Config + status + manual-fallback downloads. No live auth (gated). */}
            {currentTarget === 'uniconta' && (
            <div className="pt-3 mt-1 border-t border-border/60">
                <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Plug className="h-3.5 w-3.5" /> Uniconta connection
                    </Label>
                    <Badge variant={uniConfigured && uniCredsSet ? 'secondary' : 'outline'} className="text-[11px]"
                           data-testid={`uniconta-status-${tenant.id}`}>
                        {!uniConfigured
                            ? 'Not configured'
                            : (perCompanyCreds
                                ? 'Connected (own login)'
                                : (!ownerCredsKnown
                                    ? 'Checking…'
                                    : (ownerCredentials ? 'Connected' : 'Configured (enter login)')))}
                    </Badge>
                </div>
                <div className="mt-2">
                    <Label className="text-[11px] text-muted-foreground">Delivery mode</Label>
                    <Select value={uniMode} onValueChange={setUniMode}>
                        <SelectTrigger className="h-8 mt-1" data-testid={`uniconta-mode-${tenant.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="csv">CSV + bilag (no Uniconta licence needed)</SelectItem>
                            <SelectItem value="api">API push (requires Integrationspakke)</SelectItem>
                        </SelectContent>
                    </Select>
                    {uniPack === true && (
                        <p className="text-[11px] text-muted-foreground mt-1" data-testid={`uniconta-pack-${tenant.id}`}>
                            API push available for this company.
                        </p>
                    )}
                    {uniPack === false && (
                        <p className="text-[11px] text-amber-600 mt-1" data-testid={`uniconta-pack-${tenant.id}`}>
                            This company does not have Uniconta&apos;s Integrationspakke (approx. 25 DKK/company/month),
                            which Uniconta requires for API posting. CSV + bilag delivery below works without it.
                        </p>
                    )}
                    {uniMode === 'api' && uniPack === false && (
                        <p className="text-[11px] text-amber-600 mt-1">
                            Delivery mode is set to API push, so exports for this company will stop rather than post.
                            Switch to CSV + bilag, or use the download buttons below.
                        </p>
                    )}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                        <Label className="text-[11px] text-muted-foreground">Company ID</Label>
                        <Input className="h-8 mt-1" type="number" placeholder="e.g. 12345"
                               value={uniCompanyId} onChange={(e) => setUniCompanyId(e.target.value)}
                               data-testid={`uniconta-company-${tenant.id}`} />
                    </div>
                    <div>
                        <Label className="text-[11px] text-muted-foreground">Journal / kladde name</Label>
                        <Input className="h-8 mt-1" placeholder="e.g. Kasse"
                               value={uniJournal} onChange={(e) => setUniJournal(e.target.value)}
                               data-testid={`uniconta-journal-${tenant.id}`} />
                    </div>
                </div>
                {/* Empty login inputs are only the right thing to show when there is genuinely no
                    login. When the owner-level server user covers this company, say so and keep the
                    override one click away. */}
                {!showPerCompanyLogin ? (
                    <p className="text-[11px] text-muted-foreground mt-2"
                       data-testid={`uniconta-login-summary-${tenant.id}`}>
                        {!ownerCredsKnown
                            ? 'Checking your Uniconta login… '
                            : (ownerCredentials
                                ? 'Signed in with your Uniconta server-user login. '
                                : 'No Uniconta login set. ')}
                        <button type="button" className="underline hover:text-foreground"
                                onClick={() => setShowPerCompanyLogin(true)}
                                data-testid={`uniconta-login-override-${tenant.id}`}>
                            Use a different login for this client
                        </button>
                    </p>
                ) : (
                <div className="grid grid-cols-2 gap-2 mt-2" data-testid={`uniconta-login-fields-${tenant.id}`}>
                    <div>
                        <Label className="text-[11px] text-muted-foreground">Server-user login</Label>
                        <Input className="h-8 mt-1" placeholder="server-user username" autoComplete="off"
                               value={uniUser} onChange={(e) => setUniUser(e.target.value)}
                               data-testid={`uniconta-user-${tenant.id}`} />
                    </div>
                    <div>
                        <Label className="text-[11px] text-muted-foreground">Server-user password</Label>
                        <Input className="h-8 mt-1" type="password" autoComplete="new-password"
                               placeholder={perCompanyCreds ? '•••••••• (saved — leave blank to keep)' : 'password'}
                               value={uniPassword} onChange={(e) => setUniPassword(e.target.value)}
                               data-testid={`uniconta-pass-${tenant.id}`} />
                    </div>
                </div>
                )}
                <div className="mt-2">
                    <Label className="text-[11px] text-muted-foreground">VAT-code mapping (Accountrix → Uniconta)</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                        {Object.keys(UNICONTA_VAT_DEFAULTS).map((k) => (
                            <div key={k} className="flex items-center gap-1.5">
                                <span className="font-mono text-[11px] w-16 text-muted-foreground">{k}</span>
                                <Input className="h-7 text-xs" value={uniVat[k] || ''}
                                       onChange={(e) => setUniVat((m) => ({ ...m, [k]: e.target.value }))}
                                       data-testid={`uniconta-vat-${k}-${tenant.id}`} />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                    <Button size="sm" className="h-8" onClick={saveAndTestUniconta} disabled={uniBusy}
                            data-testid={`uniconta-connect-${tenant.id}`}>
                        {uniBusy ? 'Saving…' : 'Save & connect'}
                    </Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                    Save &amp; connect tests the live Uniconta login. The manual delivery below always works as a fallback.
                </p>
                <div className="mt-3">
                    <InboxIngestPanel tenant={tenant} onSaved={refreshTenants} />
                </div>
                {uniCanPush && (
                    <div className="mt-2 pt-2 border-t border-border/40" data-testid={`uniconta-push-block-${tenant.id}`}>
                        <Label className="text-[11px] text-muted-foreground">Bogfør direkte i Uniconta (API)</Label>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                            <Button type="button" size="sm" variant="outline" className="h-8"
                                    onClick={unicontaPreviewPush} disabled={!!uniPushing}
                                    data-testid={`uniconta-push-preview-${tenant.id}`}>
                                {uniPushing === 'preview' ? 'Henter…' : 'Forhåndsvis bogføring'}
                            </Button>
                            <Button type="button" size="sm" className="h-8"
                                    onClick={unicontaDoPush}
                                    disabled={!!uniPushing || !uniPreview || !(uniPreview.line_count > 0)}
                                    title={!uniPreview ? 'Forhåndsvis først' : undefined}
                                    data-testid={`uniconta-push-${tenant.id}`}>
                                {uniPushing === 'push' ? 'Bogfører…' : 'Bogfør nu'}
                            </Button>
                        </div>

                        {/* Dry run: exactly what WOULD post, before anything posts. */}
                        {uniPreview && (
                            <div className="mt-2 rounded-md border bg-muted/40 p-2 text-[11px] space-y-1"
                                 data-testid={`uniconta-push-previewbox-${tenant.id}`}>
                                <p className="font-medium">
                                    {uniPreview.line_count} linje(r) fra {uniPreview.doc_count ?? 0} bilag
                                    → kladde «{uniPreview.would_post?.Journal}», dato {uniPreview.would_post?.Date || '—'}
                                </p>
                                {uniPreview.unmapped_vat_codes?.length > 0 && (
                                    <p className="text-amber-700">
                                        Momskoder uden mapning: {uniPreview.unmapped_vat_codes.join(', ')}
                                    </p>
                                )}
                                {(uniPreview.would_post?.Lines || []).slice(0, 5).map((l, i) => (
                                    <div key={i} className="font-mono">
                                        {l.Voucher ? `#${l.Voucher} ` : ''}{l.Date} · {l.Account}/{l.OffsetAccount} · {l.Vat || '—'} · {l.Amount}
                                    </div>
                                ))}
                                {(uniPreview.would_post?.Lines || []).length > 5 && (
                                    <p className="text-muted-foreground">
                                        … og {uniPreview.would_post.Lines.length - 5} linje(r) mere
                                    </p>
                                )}
                                <p className="text-muted-foreground">Intet er bogført endnu.</p>
                            </div>
                        )}

                        {/* Result: what actually posted, and anything that failed. */}
                        {uniResult && (
                            <div className={`mt-2 rounded-md border p-2 text-[11px] space-y-1 ${
                                uniResult.status === 'posted' ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}
                                 data-testid={`uniconta-push-result-${tenant.id}`}>
                                <p className="font-medium">
                                    {uniResult.status === 'posted' ? 'Bogført' : 'Ikke bogført'} ·
                                    {' '}{uniResult.line_count ?? 0} linje(r) ·
                                    {' '}{uniResult.vouchers_attached ?? 0} bilag vedhæftet
                                </p>
                                {uniResult.message && <p>{uniResult.message}</p>}
                                {(uniResult.line_errors || []).map((le, i) => (
                                    <p key={i} className="text-destructive font-mono">
                                        {typeof le === 'string' ? le : JSON.stringify(le)}
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className="mt-2 pt-2 border-t border-border/40">
                    <Label className="text-[11px] text-muted-foreground">Manual delivery (fallback — works now)</Label>
                    <div className="flex items-center gap-2 mt-1">
                        <Button type="button" size="sm" variant="outline" className="h-8"
                                onClick={() => downloadUniconta('csv')} disabled={uniDownloading === 'csv'}
                                data-testid={`uniconta-csv-${tenant.id}`}>
                            <Download className="h-4 w-4 mr-1.5" /> {uniDownloading === 'csv' ? 'Preparing…' : 'Download CSV'}
                        </Button>
                        <Button type="button" size="sm" variant="outline" className="h-8"
                                onClick={() => downloadUniconta('bilag')} disabled={uniDownloading === 'bilag'}
                                data-testid={`uniconta-bilag-${tenant.id}`}>
                            <Download className="h-4 w-4 mr-1.5" /> {uniDownloading === 'bilag' ? 'Zipping…' : 'Download bilag'}
                        </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">
                        Finanskladde-import CSV + the numbered source bilag for this client's approved entries.
                    </p>
                </div>
            </div>
            )}

            {/* Danger zone: reset test data (destructive) — PLATFORM ADMIN ONLY */}
            {isAdmin && (
            <div className="mt-2 pt-3 border-t border-destructive/30">
                <Button variant="outline" size="sm"
                        className="h-8 w-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => { setResetName(''); setResetLearned(false); setResetOpen(true); }}
                        data-testid={`reset-test-data-${tenant.id}`}>
                    <Trash2 className="h-4 w-4 mr-1.5" /> Reset test data
                </Button>
            </div>
            )}

            <Dialog open={resetOpen} onOpenChange={(o) => { if (!resetting) setResetOpen(o); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-destructive">
                            <AlertTriangle className="h-5 w-5" /> Reset test data — {tenant.name}
                        </DialogTitle>
                        <DialogDescription>
                            Destructive and irreversible. This wipes only this client's working data so a clean engine test can be re-run.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
                            <p className="font-medium text-destructive mb-1">Will be wiped (this client only)</p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-0.5 text-[13px]">
                                <li>Documents + extraction, lines, bilag numbers, line audit, bank-matched flags</li>
                                <li>Vouchers created from those documents</li>
                                <li>Imported bank lines and all match state</li>
                                <li>Legacy bank-reconciliation entries (old importer) and match records</li>
                            </ul>
                        </div>
                        <div className="rounded-md border bg-muted/30 p-3">
                            <p className="font-medium mb-1">Will be kept</p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-0.5 text-[13px]">
                                <li>Accounting profile / settings (VAT profile, CVR, contra, mellemregning, scan inbox, starting bilag, bank mapping, delivery target)</li>
                                <li>The client record and all users</li>
                                <li>Every other client's data</li>
                            </ul>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer" data-testid={`reset-learned-${tenant.id}`}>
                            <Checkbox checked={resetLearned} onCheckedChange={(v) => setResetLearned(!!v)} />
                            <span>Also clear learned matching patterns (true cold-start) — default off keeps accumulated learning</span>
                        </label>
                        <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Type <span className="font-mono font-semibold text-foreground">{tenant.name}</span> to confirm</Label>
                            <Input value={resetName} onChange={(e) => setResetName(e.target.value)}
                                   placeholder={tenant.name} data-testid={`reset-confirm-${tenant.id}`} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting}>Cancel</Button>
                        <Button variant="destructive" onClick={doReset}
                                disabled={resetting || resetName.trim() !== tenant.name}
                                data-testid={`reset-confirm-btn-${tenant.id}`}>
                            {resetting ? 'Resetting…' : 'Reset test data'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function CompaniesPage() {
    const { tenants, currentTenant, selectTenant, createTenant, updateTenant, refreshTenants } = useTenant();
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editingTenant, setEditingTenant] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    // Assign an end user (client login) to a company
    const [assignTenant, setAssignTenant] = useState(null);
    // Which cards have their configuration revealed. Compact height is unaffected by this.
    const [expanded, setExpanded] = useState({});
    // {tenant_id: {review, approved, total}} — ONE aggregate call, not one per card.
    const [docCounts, setDocCounts] = useState({});
    // Whether this accountant has owner-level Uniconta credentials. Needed so a company onboarded
    // that way is not reported as unconfigured just because the card carries no credentials.
    // null until /uniconta/owner/status answers. Starting at `false` made every card paint
    // "Configured (enter login)" for a split second before correcting itself — a wrong status is
    // worse than no status, so "unknown" is its own state.
    const [ownerCredentials, setOwnerCredentials] = useState(null);
    const [assignEmail, setAssignEmail] = useState('');
    const [assigning, setAssigning] = useState(false);
    const [vatProfiles, setVatProfiles] = useState([
        { key: 'standard_vat_registered', label: 'Standard (VAT registered)' },
        { key: 'vat_exempt_passenger_transport', label: 'VAT-exempt passenger transport' },
    ]);
    const [deliveryTargets, setDeliveryTargets] = useState([
        { key: 'economic_basis', label: 'e-conomic Basis', status: 'working' },
        { key: 'economic_plus', label: 'e-conomic Plus (direct push)', status: 'working' },
        { key: 'uniconta', label: 'Uniconta', status: 'placeholder' },
    ]);
    useEffect(() => {
        accountingAPI.getClassification()
            .then((r) => {
                if (r.data?.vat_profiles?.length) setVatProfiles(r.data.vat_profiles.map((p) => ({ key: p.key, label: p.label })));
                if (r.data?.delivery_targets?.length) setDeliveryTargets(r.data.delivery_targets.map((t) => ({ key: t.key, label: t.label, status: t.status, fallback: t.fallback, fallbackNote: t.fallback_note })));
            })
            .catch(() => {});
    }, []);

    // Per-client document counts for the compact cards. One aggregate call for the whole list —
    // an accountant with 50 clients must not trigger 50 requests. Failure is silent: the counts are
    // an at-a-glance aid, never a reason to break the page.
    useEffect(() => {
        tenantAPI.getDocumentCounts()
            .then((r) => setDocCounts(r.data?.counts || {}))
            .catch(() => setDocCounts({}));
    }, [tenants.length]);

    useEffect(() => {
        unicontaOwnerAPI.status()
            .then((r) => setOwnerCredentials(!!r.data?.configured))
            .catch(() => setOwnerCredentials(false));
    }, []);

    const handleAssignEndUser = async () => {
        if (!assignTenant || !assignEmail.trim()) return;
        setAssigning(true);
        try {
            await tenantAPI.assignEndUser(assignTenant.id, assignEmail.trim());
            toast.success(`Client login ${assignEmail.trim()} linked to ${assignTenant.name}`);
            setAssignTenant(null);
            setAssignEmail('');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to assign client login');
        } finally {
            setAssigning(false);
        }
    };
    const [formData, setFormData] = useState({
        name: '',
        cvr_number: '',
        address: '',
    });

    const handleCreate = async () => {
        if (!formData.name) {
            toast.error('Company name is required');
            return;
        }

        setIsLoading(true);
        const result = await createTenant(formData);
        setIsLoading(false);

        if (result.success) {
            toast.success('Company created successfully');
            setIsCreateOpen(false);
            setFormData({ name: '', cvr_number: '', address: '' });
        } else {
            toast.error(result.error);
        }
    };

    const handleEdit = async () => {
        if (!formData.name) {
            toast.error('Company name is required');
            return;
        }

        setIsLoading(true);
        const result = await updateTenant(editingTenant.id, formData);
        setIsLoading(false);

        if (result.success) {
            toast.success('Company updated successfully');
            setIsEditOpen(false);
            setEditingTenant(null);
            setFormData({ name: '', cvr_number: '', address: '' });
        } else {
            toast.error(result.error);
        }
    };

    const openEditDialog = (tenant) => {
        setEditingTenant(tenant);
        setFormData({
            name: tenant.name,
            cvr_number: tenant.cvr_number || '',
            address: tenant.address || '',
        });
        setIsEditOpen(true);
    };

    return (
        <div className="space-y-8" data-testid="companies-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Companies</h1>
                    <p className="text-muted-foreground mt-1">
                        Manage your companies and accounting integrations
                    </p>
                </div>
                <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="create-company-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Company
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Create New Company</DialogTitle>
                            <DialogDescription>
                                Add a new company to manage its accounting documents
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Company Name *</Label>
                                <Input
                                    id="name"
                                    placeholder="Acme ApS"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    data-testid="company-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="cvr">CVR Number</Label>
                                <Input
                                    id="cvr"
                                    placeholder="12345678"
                                    maxLength={8}
                                    value={formData.cvr_number}
                                    onChange={(e) => setFormData({ ...formData, cvr_number: e.target.value })}
                                    data-testid="company-cvr-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="address">Address</Label>
                                <Input
                                    id="address"
                                    placeholder="Copenhagen, Denmark"
                                    value={formData.address}
                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    data-testid="company-address-input"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                                Cancel
                            </Button>
                            <Button onClick={handleCreate} disabled={isLoading} data-testid="submit-company-btn">
                                {isLoading ? 'Creating...' : 'Create Company'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Portfolio overview — at-a-glance delivery target + connection status per company.
                Computed client-side from the already-loaded tenants (no extra calls). */}
            {tenants.length > 0 && (
                <Card className="mb-6" data-testid="portfolio-overview">
                    <CardHeader className="py-3">
                        <CardTitle className="text-sm">Portfolio — delivery &amp; connection status</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left text-muted-foreground border-b">
                                    <th className="py-1 pr-4 font-medium">Company</th>
                                    <th className="py-1 pr-4 font-medium">Delivery target</th>
                                    <th className="py-1 font-medium">Connection status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tenants.map((t) => {
                                    const st = t.settings || {};
                                    const key = st.delivery_target || 'economic_basis';
                                    const label = (deliveryTargets.find((x) => x.key === key) || {}).label || key;
                                    // Shared with the compact card — see connectionStatus() above.
                                    const status = connectionStatus(t, { ownerCredentials: !!ownerCredentials });
                                    const warn = connectionStatusIsWarning(status);
                                    return (
                                        <tr key={t.id} className="border-b border-border/40" data-testid={`overview-row-${t.id}`}>
                                            <td className="py-1 pr-4 truncate max-w-[180px]">{t.name}</td>
                                            <td className="py-1 pr-4"><Badge variant="secondary" className="text-[10px]">{label}</Badge></td>
                                            <td className={`py-1 ${warn ? 'text-amber-600' : 'text-muted-foreground'}`}>{status}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            )}

            {/* Companies Grid */}
            {tenants.length === 0 ? (
                <Card className="border-dashed border-2" data-testid="no-companies-card">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                            <Building2 className="h-8 w-8 text-primary" />
                        </div>
                        <h3 className="font-heading text-xl font-semibold mb-2">No Companies Yet</h3>
                        <p className="text-muted-foreground text-center mb-6 max-w-md">
                            Create your first company to start managing invoices and documents
                        </p>
                        <Button onClick={() => setIsCreateOpen(true)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Create Company
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <motion.div 
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    variants={container}
                    initial="hidden"
                    animate="show"
                >
                    {tenants.map((tenant) => (
                        <motion.div key={tenant.id} variants={item}>
                            <Card 
                                className={`cursor-pointer transition-all hover:shadow-md ${
                                    currentTenant?.id === tenant.id ? 'ring-2 ring-primary' : ''
                                }`}
                                onClick={() => selectTenant(tenant)}
                                data-testid={`company-card-${tenant.id}`}
                            >
                                <CardHeader className="flex flex-row items-start justify-between gap-2">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                            <Building2 className="h-5 w-5 text-primary" />
                                        </div>
                                        <div className="min-w-0">
                                            {/* One line, ellipsised. A wrapping name would make this
                                                card taller than its neighbours; the full name stays
                                                available on hover. min-w-0 lets truncate work inside
                                                the flex row. */}
                                            <CardTitle className="text-lg truncate" title={tenant.name}>{tenant.name}</CardTitle>
                                            {tenant.cvr_number && (
                                                <p className="text-sm text-muted-foreground font-mono">
                                                    CVR: {tenant.cvr_number}
                                                </p>
                                            )}
                                            {(() => {
                                                const dt = deliveryTargets.find((t) => t.key === ((tenant.settings && tenant.settings.delivery_target) || 'economic_basis')) || { label: 'e-conomic Basis', status: 'working' };
                                                const viaCsv = dt.status !== 'working' && dt.fallback;
                                                return (
                                                    <Badge variant="secondary" className="mt-1 text-[11px]" data-testid={`delivery-target-badge-${tenant.id}`}>
                                                        {dt.label}{viaCsv ? ' · via CSV' : ''}
                                                    </Badge>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setAssignEmail('');
                                                setAssignTenant(tenant);
                                            }}
                                            title="Add client login (end user)"
                                            data-testid={`assign-enduser-${tenant.id}`}
                                        >
                                            <UserPlus className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openEditDialog(tenant);
                                            }}
                                            data-testid={`edit-company-${tenant.id}`}
                                        >
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {/* COMPACT — fixed height so every card in the grid lines up regardless
                                        of how much configuration the client has. Address is clamped rather
                                        than allowed to grow; the counts/status row sits at the bottom. */}
                                    <div className="h-[104px] flex flex-col justify-between">
                                        <p className="text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">
                                            {tenant.address || <span className="italic opacity-60">Ingen adresse</span>}
                                        </p>
                                        <div className="flex items-end justify-between gap-2">
                                            <div className="text-xs" data-testid={`doc-counts-${tenant.id}`}>
                                                {(() => {
                                                    const c = docCounts[tenant.id];
                                                    if (!c) return <span className="text-muted-foreground">&nbsp;</span>;
                                                    return (
                                                        <span className="text-muted-foreground">
                                                            <span className={c.review > 0 ? 'text-amber-600 font-medium' : ''}>
                                                                {c.review} til gennemgang
                                                            </span>
                                                            {' · '}{c.approved} godkendt
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                            {(() => {
                                                const status = connectionStatus(tenant, { ownerCredentials: !!ownerCredentials });
                                                return (
                                                    <span className={`text-[11px] whitespace-nowrap ${
                                                        connectionStatusIsWarning(status) ? 'text-amber-600' : 'text-muted-foreground'}`}
                                                          data-testid={`connection-status-${tenant.id}`}>
                                                        {status}
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between border-t pt-3 mt-3 text-xs text-muted-foreground">
                                        <span>Created {formatDate(tenant.created_at)}</span>
                                        {/* No "Active" text: it appeared on exactly one card, which read
                                            as an inconsistency. The selected card is already marked by
                                            the primary ring on the Card itself — same information, and
                                            it cannot shift the footer layout. */}
                                        <div className="flex items-center gap-3">
                                            <Button
                                                variant="ghost" size="sm" className="h-7 px-2 text-xs"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpanded((m) => ({ ...m, [tenant.id]: !m[tenant.id] }));
                                                }}
                                                data-testid={`settings-toggle-${tenant.id}`}
                                            >
                                                <Settings className="h-3.5 w-3.5 mr-1.5" />
                                                Indstillinger
                                            </Button>
                                        </div>
                                    </div>

                                    {/* EXPANDED — rendered OUTSIDE the fixed-height region, so an open card
                                        grows downward without changing any other card's compact height. */}
                                    {expanded[tenant.id] && (
                                        <div className="mt-3 pt-3 border-t" data-testid={`company-settings-${tenant.id}`}>
                                            <ClientAccountingPanel tenant={tenant} vatProfiles={vatProfiles} deliveryTargets={deliveryTargets} ownerCredentials={ownerCredentials} />
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </motion.div>
            )}

            {/* Assign client login (end user) Dialog */}
            <Dialog open={!!assignTenant} onOpenChange={(o) => { if (!o) setAssignTenant(null); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add client login</DialogTitle>
                        <DialogDescription>
                            Link a business-owner (end user) account to{assignTenant ? ` ${assignTenant.name}` : ' this company'}.
                            They'll see only this company's inbox — no accountant tools, no other clients.
                            The account must already be registered as an end user.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label htmlFor="assign-email">End user email</Label>
                        <Input
                            id="assign-email"
                            type="email"
                            placeholder="owner@business.dk"
                            value={assignEmail}
                            onChange={(e) => setAssignEmail(e.target.value)}
                            data-testid="assign-email-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAssignTenant(null)} disabled={assigning}>Cancel</Button>
                        <Button onClick={handleAssignEndUser} disabled={assigning || !assignEmail.trim()} data-testid="submit-assign-btn">
                            {assigning ? 'Linking…' : 'Link client login'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Company</DialogTitle>
                        <DialogDescription>
                            Update company information
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-name">Company Name *</Label>
                            <Input
                                id="edit-name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                data-testid="edit-company-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-cvr">CVR Number</Label>
                            <Input
                                id="edit-cvr"
                                maxLength={8}
                                value={formData.cvr_number}
                                onChange={(e) => setFormData({ ...formData, cvr_number: e.target.value })}
                                data-testid="edit-company-cvr"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-address">Address</Label>
                            <Input
                                id="edit-address"
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                data-testid="edit-company-address"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsEditOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleEdit} disabled={isLoading} data-testid="update-company-btn">
                            {isLoading ? 'Updating...' : 'Update Company'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
